import Component from 'ShopUiBundle/modules/app/component';
import breakpoints, { Breakpoints } from 'ShopUiBundle/modules/breakpoints';
import { createPopper, Instance as PopperInstance, Placement as PopperPlacement } from '@popperjs/core';
import Hammer from 'hammerjs';

export enum DROPDOWN_EVENTS {
    open = 'dropdown.open',
    close = 'dropdown.close',
    update = 'dropdown.update',
}

export default class ButtonDropdown extends Component {
    protected button: HTMLElement | null = null;
    protected buttonHammer: HammerManager | null = null;
    protected buttonTitle: HTMLElement | null = null;
    protected contentWrapper: HTMLElement | null = null;
    protected hover = true;
    protected breakpoint: string | undefined;
    protected popper: PopperInstance | null = null;
    protected openModifier = 'open';
    protected originElement: HTMLElement | null = null;
    protected allowedElements: HTMLElement[] = [];

    constructor() {
        super();

        this.onButtonTap = this.onButtonTap.bind(this);
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onBodyClick = this.onBodyClick.bind(this);
        this.onBodyMouseDown = this.onBodyMouseDown.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onAnotherDropdownOpen = this.onAnotherDropdownOpen.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
    }

    mountCallback(): void {
        this.button = this.queryElement('button');
        this.buttonTitle = this.queryElement('button-title');
        this.contentWrapper = this.queryElement('content-wrapper');

        if (!this.button || !this.contentWrapper) return;

        this.breakpoint = this.dataset?.breakpoint;
        this.hover = this.dataset?.hover !== undefined;

        this.buttonHammer = new Hammer(this.button);

        this.initPopper();
        this.enableEvents();
    }

    disconnectedCallback(): void {
        this.disableEvents();
    }

    public enableEvents(): void {
        this.disableEvents();

        if (!this.button || !this.contentWrapper) return;

        this.buttonHammer?.on('tap', this.onButtonTap);

        this.button.addEventListener('click', this.onButtonClick);
        this.button.addEventListener('mouseenter', this.onMouseEnter);
        this.button.addEventListener('mouseleave', this.onMouseLeave);

        this.contentWrapper.addEventListener('mouseenter', this.onMouseEnter);
        this.contentWrapper.addEventListener('mouseleave', this.onMouseLeave);

        document.addEventListener('keyup', this.onKeyUp);
        breakpoints.addEventListener(Breakpoints.EVENT_RESIZE, this.onResize);
    }

    public disableEvents(): void {
        this.buttonHammer?.off('tap', this.onButtonTap);

        this.button?.removeEventListener('click', this.onButtonClick);
        this.button?.removeEventListener('mouseenter', this.onMouseEnter);
        this.button?.removeEventListener('mouseleave', this.onMouseLeave);

        this.contentWrapper?.removeEventListener('mouseenter', this.onMouseEnter);
        this.contentWrapper?.removeEventListener('mouseleave', this.onMouseLeave);

        document.removeEventListener('keyup', this.onKeyUp);
        breakpoints.removeEventListener(Breakpoints.EVENT_RESIZE, this.onResize);

        this.disableOpenEvents();
    }

    public enableOpenEvents(): void {
        this.disableOpenEvents();

        document.documentElement.addEventListener('click', this.onBodyClick);
        document.documentElement.addEventListener('mousedown', this.onBodyMouseDown);
        document.documentElement.addEventListener(DROPDOWN_EVENTS.open, this.onAnotherDropdownOpen as EventListener);
    }

    public disableOpenEvents(): void {
        document.documentElement.removeEventListener('click', this.onBodyClick);
        document.documentElement.removeEventListener('mousedown', this.onBodyMouseDown);
        document.documentElement.removeEventListener(DROPDOWN_EVENTS.open, this.onAnotherDropdownOpen as EventListener);
    }

    protected onButtonTap(): void {
        if (!this.isBreakpointEnabled) return;

        this.toggle();
    }

    protected onButtonClick(e: Event): void {
        e?.preventDefault();
    }

    protected onMouseEnter(e: Event): void {
        if (!this.hover || !this.isBreakpointEnabled) return;

        this.open();
    }

    protected onMouseLeave(e: Event): void {
        if (!this.hover) return;

        this.close();
    }

    protected onBodyClick(e: Event): void {
        const target = e.target as HTMLElement;

        const originElement = this.originElement;

        this.originElement = null;

        if (!this.isOpen
            || this.button?.contains(target)
            || this.contentWrapper?.contains(target)
            || this.button?.contains(originElement)
            || this.contentWrapper?.contains(originElement)
            || this.allowedElements?.find(element => element.contains(target))) return;

        this.close();
    }

    protected onBodyMouseDown(e: Event): void {
        this.originElement = e.target as HTMLElement;
    }

    protected onAnotherDropdownOpen(e: CustomEvent): void {
        const target = e.detail?.target;

        if (this === target) return;

        this.close();
    }

    protected onKeyUp(e: KeyboardEvent): void {
        switch (e.key) {
            case 'Esc':
            case 'Escape':
                this.close();
                break;
            case 'Tab':
                if (this.contains(document.activeElement)) {
                    this.open();
                } else {
                    this.close();
                }
                break;
        }
    }

    protected onResize(e: Event): void {
        this.close();
    }

    public get isOpen(): boolean {
        return !this.contentWrapper?.hasAttribute('hidden');
    }

    public get isBreakpointEnabled(): boolean {
        if (this.breakpoint && Breakpoints.compareBreakpointByName(this.breakpoint) < 0) {
            return false;
        }

        return true;
    }

    public isMouseEvent(e: Event): boolean {
        try {
            return (e as PointerEvent).pointerType === 'mouse';
        } catch (error) {
            return true;
        }
    }

    public pushAllowedElement(element: HTMLElement) {
        if (!this.allowedElements.includes(element)) {
            this.allowedElements.push(element);
        }
    }

    protected initPopper(): void {
        if (!this.button || !this.contentWrapper) return;

        const offsetX = parseInt(this.dataset?.offsetX ?? '0');
        const offsetY = parseInt(this.dataset?.offsetY ?? '0');
        const flip = this.dataset?.flip === 'true';
        const flipFallbackPlacements = this.dataset?.flipFallbackPlacements;
        const defaultFlipFallbackPlacements = ['bottom-start', 'bottom-end'];

        const modifiers = [];

        modifiers.push({
            name: 'offset',
            options: {
                offset: [offsetX, offsetY],
            },
        });

        if (!flip || flipFallbackPlacements) {
            modifiers.push({
                name: 'flip',
                options: {
                    enabled: flip,
                    flipVariations: flip,
                    fallbackPlacements: flipFallbackPlacements ? JSON.parse(flipFallbackPlacements) : defaultFlipFallbackPlacements,
                },
            });
        }

        this.popper = createPopper(this.button, this.contentWrapper, {
            placement: this.dataset?.placement as PopperPlacement ?? 'bottom-start',
            modifiers: modifiers,
        });
    }

    public toggle():void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    public open():void {
        if (this.isOpen || !this.isBreakpointEnabled) return;

        this.lazyloadImages();
        this.update();

        this.button?.setAttribute('aria-expanded', 'true');
        this.contentWrapper?.removeAttribute('hidden');
        this.addModifier(this.openModifier);

        this.enableOpenEvents();

        const customEvent = new CustomEvent(DROPDOWN_EVENTS.open, {
            detail: {
                target: this,
            },
        });

        document.documentElement.dispatchEvent(customEvent);
        this.dispatchEvent(customEvent);
    }

    public close():void {
        if (!this.isOpen) return;

        this.button?.setAttribute('aria-expanded', 'false');
        this.contentWrapper?.setAttribute('hidden', 'hidden');
        this.removeModifier(this.openModifier);

        this.disableOpenEvents();

        const customEvent = new CustomEvent(DROPDOWN_EVENTS.close, {
            detail: {
                target: this,
            },
        });

        document.documentElement.dispatchEvent(customEvent);
        this.dispatchEvent(customEvent);
    }

    public async updateButtonTitle(title: string|null = null): Promise<void> {
        if (!this.buttonTitle) return;

        const newTitle = title || this.buttonTitle.dataset.title;

        if(newTitle) {
            this.buttonTitle.innerText = newTitle;
        }
    }

    protected async update(): Promise<void> {
        this.popper?.update();
    }

    protected async lazyloadImages(): Promise<void> {
        const items: HTMLElement[] = Array.from(this.querySelectorAll('[data-src]'));

        items.forEach(item => {
            if (item.dataset.src) {
                item.setAttribute('src', item.dataset.src);
                delete item.dataset.src;
            }
        });
    }
}
